import { useState } from "react";
import { PALETTE, FONT_BODY, FONT_DISPLAY, FONT_MONO, track } from "./tokens";

/* "Pick your industry" chip selector. Three sample moments per
   vertical — morning brief, midday action, end-of-day — themed
   to that business. Lets the SMB owner self-identify in <3s. */

type Moment = { time: string; label: string; text: string };
type Vertical = { key: string; label: string; moments: Moment[] };

const VERTICALS: Vertical[] = [
  {
    key: "trades",
    label: "Trades & contractors",
    moments: [
      { time: "07:42", label: "Multi-agent kickoff", text: "Estimator's agent drafts 3 quotes · Dispatcher's agent slots tomorrow's crew · Owner gets one approval card. All three share the same customer history." },
      { time: "12:18", label: "Cross-team signal", text: "Greene St delivery slipped — Vendor agent flagged it, Dispatch agent moved the install window, Sales agent texted the customer. CoBrain coordinated all three." },
      { time: "17:55", label: "Org rollup", text: "Today across the company: 11 follow-ups closed, 2 invoices queued, 1 dispatch bottleneck flagged. Owner sees the whole picture in one view." },
    ],
  },
  {
    key: "dental",
    label: "Dental & medical practice",
    moments: [
      { time: "07:30", label: "Shared chart prep", text: "Hygienist's agent prepped today's charts · Front-desk agent sent 4 recall reminders · Insurance agent queued 1 question for the office manager. All three on the same patient brain." },
      { time: "13:05", label: "Cross-role coordination", text: "Two cancellations · Front-desk agent rebooked into open slots, Hygienist's agent re-prepped charts, Owner-Eva noted the revenue impact." },
      { time: "18:10", label: "Practice-wide view", text: "Across both locations: 2 reschedules, 6 recalls confirmed, 1 refund needs owner sign-off. Real-time across the practice." },
    ],
  },
  {
    key: "legal",
    label: "Legal & professional",
    moments: [
      { time: "08:00", label: "Firm-wide intake", text: "Intake agent acknowledged 3 inquiries · Paralegal agents ran conflict checks · Partner-Eva flagged 1 urgent call. All visible to the whole firm." },
      { time: "14:22", label: "Matter telemetry", text: "Status updates drafted for 7 active matters across 4 attorneys. Hartwell deadline reminder routed to lead with full case context attached." },
      { time: "18:45", label: "Partner dashboard", text: "Real-time: 4 meetings prepped tomorrow, 2 invoices ready, 1 paralegal at capacity. Partners see the firm — not just their own desk." },
    ],
  },
  {
    key: "studio",
    label: "Creative studios",
    moments: [
      { time: "08:15", label: "Project-wide brief", text: "Each PM's agent drafted client check-ins · Production agent confirmed the Brewer launch assets · Finance agent cleared the Fenton invoice. Studio in sync." },
      { time: "13:40", label: "Shared client memory", text: "Round-2 feedback came in for 2 clients. Every relevant agent already knows the project history — no re-onboarding the team each round." },
      { time: "18:30", label: "Studio rollup", text: "Today: deliverables sent, 1 retainer kickoff prepped, 2 milestones queued. Creative director sees workload across the studio in one view." },
    ],
  },
  {
    key: "saas",
    label: "SaaS & software",
    moments: [
      { time: "08:00", label: "Account-wide signal", text: "CS agent caught 5 activation milestones · Sales agents got context on 2 churn-risk accounts · Founder-Eva saw the whole pipeline in one card." },
      { time: "13:15", label: "Cross-team handoff", text: "Support agent triaged 4 tickets with playbook · escalated 2 to engineering with full customer history pre-attached. No re-explaining." },
      { time: "18:00", label: "Org telemetry", text: "Real-time: CSAT trend, demo bookings, at-risk renewals, support load by team. The founder sees the company, not just the dashboards." },
    ],
  },
  {
    key: "realty",
    label: "Real estate teams",
    moments: [
      { time: "07:45", label: "Team-wide schedule", text: "Each agent's CoBrain agent confirmed showings · Marketing agent warmed 3 cold leads · Broker-Eva flagged 1 price-drop decision." },
      { time: "12:50", label: "Deal coordination", text: "Two offers organized with comp analysis. TC agent chased lenders on 4 deals. Inspector reschedule routed automatically — every party notified." },
      { time: "18:20", label: "Brokerage rollup", text: "Across the team: 3 new leads, 2 deals advanced, 1 closing tomorrow. Broker sees the whole brokerage's pipeline in real time." },
    ],
  },
];

export const IndustryResonance = () => {
  const [active, setActive] = useState(VERTICALS[0].key);
  const v = VERTICALS.find((x) => x.key === active) || VERTICALS[0];

  return (
    <div>
      {/* Chips */}
      <div role="tablist" aria-label="Pick an industry" className="cb-industry-chips" style={{
        display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24,
      }}>
        {VERTICALS.map((x) => {
          const on = x.key === active;
          return (
            <button
              key={x.key}
              role="tab"
              aria-selected={on}
              type="button"
              onClick={() => { setActive(x.key); track("industry_chip_click", { key: x.key }); }}
              style={{
                padding: "9px 16px", borderRadius: 999, cursor: "pointer",
                fontFamily: FONT_MONO, fontSize: 11.5, letterSpacing: "0.10em",
                textTransform: "uppercase", fontWeight: 700,
                background: on ? PALETTE.ink : "transparent",
                color: on ? "#fff" : PALETTE.ink2,
                border: `1px solid ${on ? PALETTE.ink : PALETTE.line}`,
                transition: "background 200ms ease, color 200ms ease, border-color 200ms ease",
              }}
            >{x.label}</button>
          );
        })}
      </div>

      {/* Vignette */}
      <div key={v.key} className="cb-industry-vignette" style={{
        background: PALETTE.card, border: `1px solid ${PALETTE.line}`,
        borderRadius: 20, padding: "clamp(20px, 3vw, 28px)",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 0, animation: "cb-fade 360ms cubic-bezier(.2,.8,.2,1) both",
      }}>
        {v.moments.map((m, i) => (
          <div key={`${v.key}-${i}`} style={{
            padding: "8px 18px",
            borderLeft: i === 0 ? "none" : `1px solid ${PALETTE.line}`,
            display: "flex", flexDirection: "column", gap: 8,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", fontSize: 22, color: PALETTE.amber, lineHeight: 1 }}>{m.time}</span>
              <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700 }}>{m.label}</span>
            </div>
            <p style={{ fontSize: 14, color: PALETTE.ink2, lineHeight: 1.55, margin: 0, fontFamily: FONT_BODY }}>{m.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
};